/**
 * pushNotifications.ts
 *
 * OneSignal push notification integration.
 *
 * Two calls, two very different timings — this is the part that matters:
 *   - initPush()                 → safe to call immediately at app start.
 *                                   Readies the SDK. Does NOT show any
 *                                   permission prompt on its own.
 *   - requestPushPermissionOnce() → shows the actual system "Allow
 *                                   Notifications?" dialog. Called ONLY
 *                                   from a real demonstrated-interest
 *                                   moment (favoriting a venue), never on
 *                                   launch — asking cold on first open is
 *                                   the single easiest way to get a
 *                                   permanent "Don't Allow" on iOS, which
 *                                   can't be re-prompted without the user
 *                                   digging into Settings themselves.
 *
 * Native-only: OneSignal's Capacitor plugin targets the wrapped iOS/
 * Android apps. Gated behind Capacitor.isNativePlatform() so this is a
 * safe no-op on the plain website in a desktop/mobile browser.
 */

import { Capacitor } from '@capacitor/core'

const ONESIGNAL_APP_ID = '0608852a-631d-4cd2-a831-570b12540d8c'
const ASKED_FLAG = 'hhu_push_permission_asked'

let initialized = false

export async function initPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (initialized) return
  try {
    const { default: OneSignal } = await import('@onesignal/capacitor-plugin')
    // NOTE: initialize() takes the App ID as a plain string, not
    // { appId: ... } — an earlier version of this file used the object
    // form based on a doc example that didn't match the actual installed
    // package's type signature. TypeScript caught the mismatch at build
    // time (TS2345) before it ever reached production.
    await OneSignal.initialize(ONESIGNAL_APP_ID)
    initialized = true
  } catch (e) {
    // Never let a push-SDK hiccup break the app
    console.error('[pushNotifications] init failed:', e)
  }
}

/**
 * Ask for push permission — but only once, ever, per device, and only
 * after init has succeeded. Safe to call from multiple places (e.g.
 * every "favorite added" action); the localStorage flag makes every
 * call after the first a no-op.
 */
export async function requestPushPermissionOnce(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (!initialized) return
  try {
    if (localStorage.getItem(ASKED_FLAG)) return
    localStorage.setItem(ASKED_FLAG, '1')  // set BEFORE asking — never re-prompt even if this call throws
    const { default: OneSignal } = await import('@onesignal/capacitor-plugin')
    await OneSignal.Notifications.requestPermission(true)
  } catch (e) {
    console.error('[pushNotifications] permission request failed:', e)
  }
}
